import type { JSX, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AsyncState } from '@/components/async';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { PillButton } from '@/components/entity/PillButton';
import { PropertyDeleteButton } from '@/components/dashboard/properties';
import { useDashboardPropertyDetail } from '@/hooks/useDashboardProperties';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { formatAssetTypeLabel } from '@/lib/constants/propertyOptions';
import { formatPropertyAddress, formatSquareFeet } from '@/lib/utils/propertyFormatting';
import { PropertyUnitsList } from '@/routes/dashboard/propertyDetail/PropertyUnitsList';

const propertyDetailSkeleton = (
  <div className="flex flex-col gap-4">
    <div className="bg-surface-muted h-28 animate-pulse rounded-3xl border border-surface-subtle" />
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="bg-surface-muted h-56 animate-pulse rounded-3xl border border-surface-subtle" />
        <div className="bg-surface-muted h-40 animate-pulse rounded-3xl border border-surface-subtle" />
      </div>
    </div>
  </div>
);

function renderPropertyFact(label: string, value: ReactNode): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs uppercase tracking-wide text-text-primary/60">{label}</dt>
      <dd className="text-sm font-semibold text-text-primary">{value}</dd>
    </div>
  );
}

function renderPropertyDetailError(onBack: () => void): JSX.Element {
  return (
    <div className="border-destructive/30 bg-destructive/5 rounded-3xl border p-6 text-sm text-text-primary shadow-sm">
      <p className="text-destructive text-base font-semibold">Unable to load property</p>
      <p className="mt-2 text-text-primary/80">
        We could not load this property. Return to the properties list and try again.
      </p>
      <div className="mt-4">
        <PillButton onClick={onBack}>Back to properties</PillButton>
      </div>
    </div>
  );
}

export function DashboardPropertyDetail(): JSX.Element {
  const user = useDashboardUser();
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();

  const normalizedPropertyId = (propertyId ?? '').trim();
  const detailQuery = useDashboardPropertyDetail({
    user,
    propertyId: normalizedPropertyId.length > 0 ? normalizedPropertyId : null,
  });

  const backToProperties = () => {
    navigate('/dashboard/properties');
  };

  return (
    <section className="flex flex-col gap-xl py-8">
      <button
        type="button"
        onClick={backToProperties}
        className="min-h-11 w-fit rounded-full border border-brand-primary/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:bg-brand-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
      >
        Back to properties
      </button>

      <AsyncState
        data={detailQuery.data ?? null}
        isLoading={detailQuery.isLoading || detailQuery.isFetching}
        error={detailQuery.error}
        skeleton={propertyDetailSkeleton}
        errorFallback={renderPropertyDetailError(backToProperties)}
      >
        {(detail) => {
          const propertyLabel = formatPropertyAddress(detail.property.address);
          const unitCount = detail.units?.length ?? 0;
          const units = detail.units ?? [];

          return (
            <div className="flex flex-col gap-6">
              <header className="flex flex-col gap-4 rounded-3xl border border-surface-subtle bg-white p-6 shadow-sm md:flex-row md:items-start md:justify-between">
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
                    Property details
                  </p>
                  <h1 className="text-3xl font-semibold text-text-primary">{propertyLabel}</h1>
                  <p className="text-sm text-text-primary/70">{detail.property.marketingSummary}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <StatusBadge
                      tone={detail.summary.hasVacancy ? 'success' : 'neutral'}
                      label={detail.summary.hasVacancy ? 'Vacancy' : 'No vacancy'}
                    />
                    <StatusBadge tone="neutral" label={`${unitCount.toString()} units`} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <PillButton
                    variant="secondary"
                    onClick={() => {
                      navigate(`/dashboard/properties/${detail.summary.id}/edit`);
                    }}
                  >
                    Edit property
                  </PillButton>
                  <PillButton
                    onClick={() => {
                      navigate(`/dashboard/properties/${detail.summary.id}/units/new`);
                    }}
                  >
                    Add unit
                  </PillButton>
                  <PropertyDeleteButton
                    property={detail.summary}
                    variant="secondary"
                    onDeleted={backToProperties}
                  />
                </div>
              </header>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-4">
                  <div className="rounded-3xl border border-surface-subtle bg-white p-5 shadow-sm">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
                      Property facts
                    </h2>
                    <dl className="mt-4 grid gap-4 sm:grid-cols-2">
                      {renderPropertyFact('Property code', detail.property.propertyCode)}
                      {renderPropertyFact('Submarket', detail.property.submarket)}
                      {renderPropertyFact(
                        'Asset type',
                        formatAssetTypeLabel(detail.property.assetType),
                      )}
                      {renderPropertyFact(
                        'Total rentable SF',
                        formatSquareFeet(detail.property.totalRentableSf),
                      )}
                    </dl>
                  </div>
                </div>
                <div className="rounded-3xl border border-surface-subtle bg-white p-5 shadow-sm">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
                    Hero media
                  </h2>
                  {detail.property.heroMediaUrls.length > 0 ? (
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      {detail.property.heroMediaUrls.map((url) => (
                        <img
                          key={url}
                          src={url}
                          alt="Property hero"
                          className="h-40 w-full rounded-2xl border border-surface-subtle object-cover"
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-text-primary/70">
                      No hero images yet. Add them from the Edit property page.
                    </p>
                  )}
                </div>
              </div>

              <PropertyUnitsList propertyId={detail.summary.id} units={units} />
            </div>
          );
        }}
      </AsyncState>
    </section>
  );
}
